import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { supabase } from '../../supabase';

const { FiSend, FiCheckCircle, FiLoader, FiPhone, FiMail, FiUser, FiTag, FiMessageSquare } = FiIcons;

const ContactForm = ({ preselectedInterest = "", onSuccess = () => {} }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [interests, setInterests] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    eventType: preselectedInterest,
    message: ''
  });

  useEffect(() => {
    fetchServiceTitles();
  }, []);

  useEffect(() => {
    if (preselectedInterest) {
      setFormData(prev => ({ ...prev, eventType: preselectedInterest }));
    }
  }, [preselectedInterest]);

  const fetchServiceTitles = async () => {
    try {
      const { data } = await supabase
        .from('service_links_1740486000000')
        .select('title')
        .order('title');
      if (data) setInterests(data.map(item => item.title));
    } catch (err) {
      console.error("Error fetching interests:", err);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Webhook URL provided by the user
    const MAKE_WEBHOOK_URL = "https://hook.us2.make.com/sps9w5vq8dwtbwpfw3afbewlh2fpwkcu";

    try {
      // 1. Save to Database for the Admin Dashboard
      const { error: dbError } = await supabase
        .from('contacts_1740500000000')
        .insert([{
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          event_type: formData.eventType,
          message: formData.message,
          is_read: false
        }]);

      if (dbError) throw dbError;

      // 2. Send to Make.com Webhook
      // We use a clean payload structure that is easy for Make.com to parse
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || "Not provided",
        interest: formData.eventType,
        message: formData.message,
        submittedAt: new Date().toLocaleString(),
        source: 'Canvas & Clarity Website',
        recipient: 'admin@ijourney.us'
      };

      const webhookResponse = await fetch(MAKE_WEBHOOK_URL, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!webhookResponse.ok) {
        console.warn("Webhook responded with an error, but data was saved to DB.");
      }

      setIsSuccess(true);
      onSuccess();
      setFormData({ name: '', email: '', phone: '', eventType: '', message: '' });
      setTimeout(() => setIsSuccess(false), 5000);
    } catch (err) {
      console.error('Submission error:', err);
      alert('We received an error, but you can also email us directly at admin@ijourney.us');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative h-full">
      <AnimatePresence mode="wait">
        {isSuccess ? (
          <motion.div
            key="success"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-green-50 p-10 rounded-[2.5rem] border border-green-100 text-center h-full flex flex-col items-center justify-center shadow-inner"
          >
            <SafeIcon icon={FiCheckCircle} className="w-16 h-16 text-green-500 mb-6" />
            <h3 className="text-2xl font-bold text-gray-800 mb-2">Inquiry Sent!</h3>
            <p className="text-gray-600">We will get back to you shortly.</p>
          </motion.div>
        ) : (
          <motion.form
            key="form"
            onSubmit={handleSubmit}
            className="space-y-6"
          >
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-1">
                <label className="text-[10px] font-black uppercase text-gray-400 tracking-widest ml-1">Your Name</label>
                <div className="relative">
                  <SafeIcon icon={FiUser} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" />
                  <input type="text" name="name" value={formData.name} onChange={handleInputChange} placeholder="Jane Doe" required className="w-full pl-11 p-4 rounded-2xl border-none ring-1 ring-gray-200 focus:ring-2 focus:ring-rose-500 outline-none transition-all" />
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-black uppercase text-gray-400 tracking-widest ml-1">Email Address</label>
                <div className="relative">
                  <SafeIcon icon={FiMail} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" />
                  <input type="email" name="email" value={formData.email} onChange={handleInputChange} placeholder="jane@example.com" required className="w-full pl-11 p-4 rounded-2xl border-none ring-1 ring-gray-200 focus:ring-2 focus:ring-rose-500 outline-none transition-all" />
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-1">
                <label className="text-[10px] font-black uppercase text-gray-400 tracking-widest ml-1">Phone (Optional)</label>
                <div className="relative">
                  <SafeIcon icon={FiPhone} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" />
                  <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} placeholder="(555) 000-0000" className="w-full pl-11 p-4 rounded-2xl border-none ring-1 ring-gray-200 focus:ring-2 focus:ring-rose-500 outline-none transition-all" />
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-black uppercase text-gray-400 tracking-widest ml-1">Interest</label>
                <div className="relative">
                  <SafeIcon icon={FiTag} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" />
                  <select name="eventType" value={formData.eventType} onChange={handleInputChange} required className="w-full pl-11 p-4 rounded-2xl border-none ring-1 ring-gray-200 focus:ring-2 focus:ring-rose-500 outline-none transition-all bg-white" >
                    <option value="">Select Service</option>
                    {interests.map((title, i) => (
                      <option key={i} value={title}>{title}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-black uppercase text-gray-400 tracking-widest ml-1">Message</label>
              <div className="relative">
                <SafeIcon icon={FiMessageSquare} className="absolute left-4 top-4 text-gray-300" />
                <textarea name="message" value={formData.message} onChange={handleInputChange} rows="4" placeholder="Tell us about your heart's vision..." required className="w-full pl-11 p-4 rounded-2xl border-none ring-1 ring-gray-200 focus:ring-2 focus:ring-rose-500 outline-none transition-all resize-none shadow-sm" ></textarea>
              </div>
            </div>

            <button type="submit" disabled={isSubmitting} className="w-full bg-gradient-to-r from-rose-500 to-purple-600 text-white py-4 rounded-2xl font-bold flex items-center justify-center space-x-2 hover:shadow-lg hover:shadow-rose-200 transition-all disabled:opacity-50" >
              {isSubmitting ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={FiSend} />}
              <span className="text-lg">{isSubmitting ? 'Sending...' : 'Book My Experience'}</span>
            </button>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ContactForm;